/**
 * CeremonyDocumentViewer — the read-only document review surface of the v2 signing ceremony
 * (CB-5.4, FR-49 / NFR-14 / UX-DR26 / UX-DR28; AD-15/16).
 *
 * Renders EVERY page of the contract PDF so the signer can review the full document before signing.
 * It reuses the editor's per-page render substrate `PdfPageCanvas` (CB-2.2 / AD-15) — the renderer is
 * NOT forked; only the host (lazy IntersectionObserver + box reservation) is a read-only variant
 * (no draggable field overlay — that's CB-2.3/5.6).
 *
 * Accessibility (NFR-14, a story requirement here — not deferred to CB-5.11):
 *  - Zoom is never disabled (the public route's viewport carries no maximum-scale/user-scalable=no);
 *    native page pinch-zoom works, AND an in-document zoom control re-renders CRISP via pdfjs (not a
 *    blurry CSS upscale) to ≥200%.
 *  - The page chrome reflows in a single column at 320px; only the page canvas itself may scroll
 *    horizontally when zoomed (WCAG 1.4.10 2D-layout exception).
 *  - Text-PDF content is exposed to assistive tech via a per-page sr-only text region (from pdfjs
 *    getTextContent). A scanned PDF with no text layer (UX-DR28) shows a calm, honest fallback notice
 *    — it does not silently block a screen-reader user; the send-time sender warning (CB-2.x/4.1)
 *    owns informing the sender. The PDF renderer is not duplicated and no OCR is performed here.
 *
 * `onReviewed` fires once the signer has reached the end of the document — the signal the CB-5.3
 * reasonable-demonstration-of-access gate consumes.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { ZoomIn, ZoomOut, FileWarning } from 'lucide-react';
import '@/lib/pdfWorker'; // bundled offline-safe worker (matches installed pdfjs version)
import { PdfPageCanvas } from '@/components/contracts/editor/PdfPageCanvas';
import { contractService } from '@/services/contractService';

interface CeremonyDocumentViewerProps {
  token: string;
  accent: string;
  /** Fired once when the signer has reviewed to the end of the document (demonstration of access). */
  onReviewed?: () => void;
  /** Tailwind max-height for the scroll region (e.g. the consent gate uses a bounded preview). */
  maxHeightClass?: string;
  /** CB-5.6: render an absolutely-positioned overlay (field chips) over each page. Receives the
   *  page's current rendered pixel size so consumers position via the shared coord util. The layer
   *  is mounted inside the page box (position: relative), so it stays registered across zoom. */
  renderPageOverlay?: (pageIndex: number, pageWidthPx: number, pageHeightPx: number) => React.ReactNode;
}

interface PageSize {
  width: number;
  height: number;
}

const PRERENDER_MARGIN = '600px 0px'; // render pages a little before they enter the viewport
const ZOOM_STEPS = [1, 1.5, 2, 3];
// Below this total character count across the document we treat it as a scanned/no-text-layer PDF.
const TEXT_LAYER_MIN_CHARS = 20;

export default function CeremonyDocumentViewer({
  token,
  accent,
  onReviewed,
  maxHeightClass = 'max-h-[70vh]',
  renderPageOverlay,
}: CeremonyDocumentViewerProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const reviewedFiredRef = useRef(false);

  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [pageSizes, setPageSizes] = useState<PageSize[]>([]);
  const [pageTexts, setPageTexts] = useState<string[]>([]);
  const [hasTextLayer, setHasTextLayer] = useState<boolean>(true);
  const [colWidth, setColWidth] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [activePages, setActivePages] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const fireReviewed = useCallback(() => {
    if (reviewedFiredRef.current) return;
    reviewedFiredRef.current = true;
    onReviewed?.();
  }, [onReviewed]);

  // ── Load the signed URL, then the PDF; pre-read page sizes + text for box reservation + AT ──
  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setLoading(true);
    setError(null);
    setUnavailable(false);
    reviewedFiredRef.current = false;

    (async () => {
      const urlResult = await contractService.getDocumentUrlV2(token);
      if (cancelled) return;
      if (!urlResult.success || !urlResult.data) {
        setError(urlResult.error || "We couldn't load the document.");
        setLoading(false);
        return;
      }
      if (!urlResult.data.available || !urlResult.data.url) {
        setUnavailable(true);
        setLoading(false);
        return;
      }
      try {
        loaded = await pdfjsLib.getDocument({ url: urlResult.data.url }).promise;
        if (cancelled) {
          await loaded.destroy();
          return;
        }
        const sizes: PageSize[] = [];
        const texts: string[] = [];
        let totalChars = 0;
        for (let i = 1; i <= loaded.numPages; i++) {
          const page = await loaded.getPage(i);
          const vp = page.getViewport({ scale: 1 });
          sizes.push({ width: vp.width, height: vp.height });
          const tc = await page.getTextContent();
          const text = tc.items
            .map((it) => ('str' in it ? (it as { str: string }).str : ''))
            .join(' ')
            .trim();
          texts.push(text);
          totalChars += text.length;
        }
        if (cancelled) {
          await loaded.destroy();
          return;
        }
        setPdf(loaded);
        setPageSizes(sizes);
        setPageTexts(texts);
        setHasTextLayer(totalChars >= TEXT_LAYER_MIN_CHARS);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('CeremonyDocumentViewer: failed to load PDF', err);
        setError("We couldn't open this document. Please try reloading the page.");
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      loaded?.destroy().catch(() => {});
    };
  }, [token]);

  // ── Track the scroll-column width so pages fit by default (re-scales on resize) ──
  //
  // ⚠️ THIS OBSERVES THE ELEMENT WHOSE SIZE IT INDIRECTLY CONTROLS, so it is one
  // sloppy measurement away from an infinite resize loop. It had two, and together
  // they made the whole ceremony visibly vibrate on some documents:
  //
  //   1. The container was `overflow-auto`, so its width DROPS by the scrollbar
  //      width the moment content overflows vertically. Page height is derived from
  //      the width (renderHeight = renderWidth / size.width * size.height), and
  //      total content height is the sum of page heights. So: width W -> pages sized
  //      W -> content taller -> scrollbar appears -> width W-15 -> pages smaller ->
  //      content shorter -> scrollbar goes -> width W -> forever. It only fires when
  //      content height sits near the scrollbar threshold, which is why it depended
  //      on the document and the viewport. Fixed on the element itself, below, by
  //      reserving the gutter permanently so width no longer depends on height.
  //
  //   2. The mount measurement used `el.clientWidth`, which INCLUDES the px-3
  //      padding, while the observer reports `contentRect.width`, which EXCLUDES it.
  //      Those disagree by 24px, so mounting always cost at least one extra
  //      re-render at the wrong scale. Dropped: ResizeObserver fires an initial
  //      callback on observe(), so the RO alone sets the first value and there is
  //      exactly one measurement semantic.
  //
  // The >= 1px guard is belt and braces against sub-pixel churn (fractional layout
  // widths at some zoom levels) re-rendering every page for nothing.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const next = entry.contentRect.width;
        setColWidth((prev) => (Math.abs(prev - next) >= 1 ? next : prev));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [pdf]);

  // ── Lazy render: mark in/near-viewport pages active (mirrors the editor host, read-only) ──
  useEffect(() => {
    const root = scrollRef.current;
    if (!pdf || pageSizes.length === 0 || !root) return;
    const io = new IntersectionObserver(
      (entries) => {
        setActivePages((prev) => {
          const next = new Set(prev);
          for (const entry of entries) {
            const idx = Number((entry.target as HTMLElement).dataset.pageIndex);
            if (Number.isNaN(idx)) continue;
            if (entry.isIntersecting) next.add(idx);
            else next.delete(idx);
          }
          return next;
        });
      },
      { root, rootMargin: PRERENDER_MARGIN, threshold: 0.01 },
    );
    pageRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [pdf, pageSizes.length]);

  // ── Demonstration-of-access: fire onReviewed when the end of the document is reached ──
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!pdf || !root || !sentinel) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) fireReviewed();
      },
      { root, threshold: 0.01 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [pdf, fireReviewed]);

  // The width each page renders at: fit-to-column × zoom. Pages wider than the column scroll
  // horizontally inside the viewer (the 1.4.10 2D-layout exception) — the chrome never overflows.
  const displayWidth = colWidth > 0 ? Math.round(colWidth * zoom) : 0;
  const zoomIndex = ZOOM_STEPS.indexOf(zoom);

  if (loading) {
    return (
      <div className={`flex ${maxHeightClass} items-center justify-center rounded-xl border border-slate-200 bg-slate-50`}>
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200"
          style={{ borderTopColor: accent }}
          role="status"
          aria-label="Loading the document"
        />
      </div>
    );
  }

  if (unavailable || error) {
    // Graceful, honest fallback — never a raw error (UX-DR20/28).
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center" role="status">
        <FileWarning className="mx-auto mb-2 h-6 w-6 text-slate-400" aria-hidden="true" />
        <p className="text-sm text-slate-600">
          {unavailable
            ? "The document isn't available to preview here. Please contact the sender if you need a copy before signing."
            : error}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      {/* Zoom control — crisp pdfjs re-render at scale (not a blurry CSS upscale). */}
      <div className="flex items-center justify-end gap-2 border-b border-slate-100 px-3 py-2">
        <span className="mr-auto text-xs text-slate-500">
          {pageSizes.length} page{pageSizes.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => setZoom(ZOOM_STEPS[Math.max(0, zoomIndex - 1)])}
          disabled={zoomIndex <= 0}
          aria-label="Zoom out"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40"
        >
          <ZoomOut className="h-4 w-4" aria-hidden="true" />
        </button>
        <span className="w-12 text-center text-sm tabular-nums text-slate-600" aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, zoomIndex + 1)])}
          disabled={zoomIndex >= ZOOM_STEPS.length - 1}
          aria-label="Zoom in"
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-40"
        >
          <ZoomIn className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {!hasTextLayer ? (
        <p
          className="flex items-start gap-2 border-b border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          role="note"
        >
          <FileWarning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          This appears to be a scanned document without selectable text, so a screen reader may not be
          able to read it. If you need an accessible copy, contact the sender.
        </p>
      ) : null}

      {/* Scrollable document region. Vertical scroll through all pages; horizontal scroll only when
          zoomed beyond the column width (2D-layout exception).

          `overflow-y-scroll`, NOT `overflow-auto`: the vertical scrollbar gutter is
          reserved permanently so this element's width stops depending on its own
          content height. That is what breaks the resize feedback loop described on
          the ResizeObserver above — with `auto`, the scrollbar appearing shrank the
          width, which shrank the pages, which removed the scrollbar, forever. The
          gutter is always visible here anyway: a contract is more than one screen. */}
      <div
        ref={scrollRef}
        className={`overflow-x-auto overflow-y-scroll [scrollbar-gutter:stable] ${maxHeightClass} px-3 py-3`}
        aria-label="Contract document"
      >
        {pageSizes.map((size, idx) => {
          const renderWidth = displayWidth > 0 ? displayWidth : size.width;
          const renderHeight = (renderWidth / size.width) * size.height;
          return (
            <div
              key={idx}
              ref={(el) => {
                pageRefs.current[idx] = el;
              }}
              data-page-index={idx}
              className="relative mx-auto mb-3"
              style={{ width: renderWidth, height: renderHeight }}
            >
              {pdf ? (
                <PdfPageCanvas
                  pdf={pdf}
                  pageIndex={idx}
                  displayWidth={renderWidth}
                  active={activePages.has(idx)}
                />
              ) : null}
              {/* Text-PDF content exposed to assistive tech (visually hidden; the canvas shows it). */}
              {hasTextLayer && pageTexts[idx] ? (
                <span className="sr-only">{pageTexts[idx]}</span>
              ) : null}
              {/* CB-5.6: field overlay layer (chips) positioned via the shared coord util. */}
              {renderPageOverlay ? renderPageOverlay(idx, renderWidth, renderHeight) : null}
            </div>
          );
        })}
        {/* Bottom sentinel: reaching it = the signer reviewed to the end (demonstration of access). */}
        <div ref={sentinelRef} aria-hidden="true" className="h-1 w-full" />
      </div>
    </div>
  );
}
